// ---------------------------------------------------------------------------
// Expression Lab drawer — 3-pane: Context (seeded, editable JSON) /
// Expression (autocomplete input) / Result (verdict chip + value + resolved
// inputs). ResourcesDrawer pattern: store-driven, absolute right panel inside
// the IDE shell — NOT a new top tab (strategy §8.1).
// ---------------------------------------------------------------------------
import { useEffect, useState } from 'react'
import { X, Play, Check } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import { slotFor } from '@/store/data-slice'
import { toast } from '@/store/toast-store'
import { cn } from '@/lib/utils'
import { VariableAutocomplete } from '@/features/flow-editor/components/inspector/forms/variable-autocomplete'
import {
  evaluateExpression,
  type ExpressionEvalResult,
} from '../services/expression-eval'
import { seedCanonicalContext } from '../services/context-seed'

const VERDICT_STYLES: Record<string, string> = {
  pass: 'bg-success/15 text-success border-success/30',
  empty: 'bg-muted text-muted-foreground border-border',
  'legacy-prefix': 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  'unknown-prefix': 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  'invalid-accessor': 'bg-red-500/15 text-red-600 border-red-500/30',
  'runtime-error': 'bg-red-500/15 text-red-600 border-red-500/30',
}

export function ExpressionLabDrawer() {
  const open = useStudioStore((s) => s.expressionLabOpen)
  const request = useStudioStore((s) => s.expressionLabRequest)
  const close = useStudioStore((s) => s.closeExpressionLab)
  const updateVariable = useStudioStore((s) => s.updateVariable)

  const [expression, setExpression] = useState('')
  const [contextJson, setContextJson] = useState('')
  const [contextError, setContextError] = useState<string | null>(null)
  const [result, setResult] = useState<ExpressionEvalResult | null>(null)

  // Seed ONLY on open/request transitions, reading the project as a
  // non-reactive snapshot. Subscribing to currentProject here (or depending
  // on a derived seed object) would re-fire this reset on every incidental
  // project mutation while the drawer is open — chat/agent edits are live
  // behind the drawer — silently clobbering the user's in-progress
  // expression and context edits (review finding, Task 5).
  useEffect(() => {
    if (!open) return
    const snapshot = seedCanonicalContext(
      useStudioStore.getState().currentProject,
      request?.applyTarget?.blockName ?? null,
    )
    setExpression(request?.expression ?? '')
    setContextJson(JSON.stringify(snapshot, null, 2))
    setContextError(null)
    setResult(null)
  }, [open, request])

  if (!open) return null

  const run = () => {
    try {
      const ctx = JSON.parse(contextJson)
      setContextError(null)
      setResult(evaluateExpression(expression, ctx))
    } catch (e) {
      setContextError(e instanceof Error ? e.message : String(e))
      setResult(null)
    }
  }

  const applyTarget = request?.applyTarget ?? null
  const canApply = applyTarget && result?.verdict === 'pass'

  const apply = () => {
    if (!applyTarget || !canApply) return
    updateVariable(applyTarget.blockName, applyTarget.scope, applyTarget.name, {
      expression,
    })
    // updateVariable silently no-ops on a missing block/slot/variable (stale
    // target after a concurrent delete/scope-move, or a chat fence naming a
    // nonexistent target) — read back before claiming success. A false
    // "applied" receipt is exactly the class the Lab exists to prevent.
    const project = useStudioStore.getState().currentProject as unknown as {
      blocks?: Record<
        string,
        { state?: Record<string, Record<string, { expression?: unknown }>> }
      >
    } | null
    const applied =
      project?.blocks?.[applyTarget.blockName]?.state?.[
        slotFor(applyTarget.scope)
      ]?.[applyTarget.name]?.expression
    if (applied !== expression) {
      toast.error(
        `Apply failed — ${applyTarget.blockName}.${applyTarget.name} was not found (deleted or moved?)`,
      )
      return
    }
    toast.success(
      `Expression applied to ${applyTarget.blockName}.${applyTarget.name}`,
    )
    close()
  }

  return (
    <aside
      aria-label="Expression Lab"
      data-testid="expression-lab-drawer"
      className="absolute inset-y-0 right-0 z-30 flex w-full max-w-[560px] flex-col gap-3 border-l border-border bg-background p-4 shadow-2xl overflow-y-auto"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Expression Lab
        </span>
        <button
          type="button"
          data-testid="expression-lab-close"
          onClick={close}
          aria-label="Close Expression Lab"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Context pane — seeded from the live project, user-editable */}
      <div className="space-y-1">
        <div className="text-[10px] font-medium uppercase text-muted-foreground">
          Context (seeded from project — editable)
        </div>
        <textarea
          data-testid="expression-lab-context"
          value={contextJson}
          onChange={(e) => setContextJson(e.target.value)}
          spellCheck={false}
          className="h-44 w-full rounded border border-border bg-muted/30 p-2 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {contextError && (
          <div className="text-[10px] text-red-600">context JSON: {contextError}</div>
        )}
      </div>

      {/* Expression pane */}
      <div className="space-y-1">
        <div className="text-[10px] font-medium uppercase text-muted-foreground">
          Expression
        </div>
        <div data-testid="expression-lab-input">
          <VariableAutocomplete
            mode="expression"
            value={expression}
            onChange={setExpression}
            placeholder="#{local.name.toUpperCase()}"
            testid="expression-lab-input-field"
          />
        </div>
        <button
          type="button"
          data-testid="expression-lab-run"
          onClick={run}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:bg-foreground/90"
        >
          <Play className="h-3 w-3" /> Evaluate
        </button>
      </div>

      {/* Result pane */}
      {result && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <span
            data-testid="expression-lab-verdict"
            className={cn(
              'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase',
              VERDICT_STYLES[result.verdict] ?? VERDICT_STYLES.empty,
            )}
          >
            {result.verdict}
          </span>
          {result.verdict === 'pass' && (
            <pre
              data-testid="expression-lab-value"
              className="overflow-x-auto rounded bg-muted/40 p-2 font-mono text-xs"
            >
              {result.formattedValue}
            </pre>
          )}
          {result.messages.map((message, i) => (
            <div key={i} className="text-[11px] text-muted-foreground">
              {message}
            </div>
          ))}
          {result.resolvedInputs.length > 0 && (
            <div className="space-y-0.5">
              <div className="text-[10px] font-medium uppercase text-muted-foreground">
                Resolved inputs
              </div>
              {result.resolvedInputs.map((input) => (
                <div key={input.path} className="font-mono text-[11px]">
                  {input.path} → {JSON.stringify(input.value) ?? 'undefined'}
                </div>
              ))}
            </div>
          )}
          {canApply && (
            <button
              type="button"
              data-testid="expression-lab-apply"
              onClick={apply}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:bg-foreground/90"
            >
              <Check className="h-3 w-3" /> Apply to {applyTarget?.name}
            </button>
          )}
        </div>
      )}
    </aside>
  )
}
